import type { Game, Save, Stats, Tx, Wallet } from './types.ts';

export const START = 5000;

const makeStats = (): Stats => ({
  wagers: 0, winnings: 0, losses: 0, games: 0, wins: 0,
  biggestWin: 0, biggestLoss: 0, highest: START, lowest: START, streak: 0,
  longestStreak: 0, lossStreak: 0, longestLossStreak: 0, byGame: {},
});
const makeWallet = (): Wallet => ({ balance: START, stats: makeStats(), history: [], rounds: [] });

export const fresh = (): Save => ({
  version: 3, mode: 'regular',
  wallets: { creative: makeWallet(), regular: makeWallet(), competitive: makeWallet() },
  owned: ['classic-ball', 'midnight-cards', 'gold-coin'],
  equipped: { ball: 'classic-ball', cards: 'midnight-cards', coin: 'gold-coin' },
  xp: 0, sound: true, reducedMotion: false, achievements: [], milestones: [], settledRounds: [],
  balancePoints: { creative: [START], regular: [START], competitive: [START] },
  blackjackRules:{decks:1,dealerHitsSoft17:false,penetration:.75,blackjackProfit:1.5},creativeFundLimit:100_000,
  createdAt: Date.now(), lastSeenAt: Date.now(),
});

export const money = (n: number) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(n);
export const random = () => {
  const a = new Uint32Array(1);
  crypto.getRandomValues(a);
  return a[0] / 4294967296;
};
const makeId = (): string => crypto.randomUUID?.() ?? `${Date.now()}-${random()}`;

export function validateBet(balance: number, bet: number) {
  if (!Number.isFinite(bet) || bet < .01) return 'Bet must be at least $0.01';
  if (bet > balance) return 'Not enough Stack Cash';
  return '';
}

export function transact(save: Save, amount: number, source: string, description: string) {
  const wallet = save.wallets[save.mode];
  const before = wallet.balance;
  const after = Math.max(0, Math.round((before + amount) * 100) / 100);
  const tx: Tx = { id: makeId(), time: Date.now(), source, amount, before, after, mode: save.mode, description };
  wallet.balance = after;
  wallet.history = [tx, ...wallet.history].slice(0, 100);
  wallet.stats.highest = Math.max(wallet.stats.highest, after);
  wallet.stats.lowest = Math.min(wallet.stats.lowest, after);
  save.balancePoints[save.mode] = [...save.balancePoints[save.mode].slice(-59), after];
}

export function prestigeMultiplier(save: Save) {
  if (save.mode !== 'regular') return 1;
  const bonuses: Record<string, number> = { '100k': .01, '1m': .02, '10m': .03, '100m': .04, '1b': .05 };
  return Math.min(1.15, 1 + save.milestones.reduce((sum, id) => sum + (bonuses[id] ?? 0), 0));
}

export function settle(save: Save, game: Game, bet: number, payout: number, roundId = makeId(), refund = false) {
  if (save.settledRounds.includes(roundId)) throw Error('This round was already settled');
  const wallet = save.wallets[save.mode];
  const error = validateBet(wallet.balance, bet);
  if (error) throw Error(error);
  if (!Number.isFinite(payout) || payout < 0) throw Error('Invalid payout');
  transact(save, -bet, game, `${game} wager`);
  const adjustedPayout = payout > bet && !refund ? bet + (payout - bet) * prestigeMultiplier(save) : payout;
  if (adjustedPayout > 0) transact(save, adjustedPayout, game, `${game} payout`);
  save.settledRounds = [...save.settledRounds.slice(-499), roundId];
  wallet.rounds=[{id:roundId,time:Date.now(),game,bet,payout:adjustedPayout,net:adjustedPayout-bet,multiplier:bet?Number((adjustedPayout/bet).toFixed(4)):0,mode:save.mode},...wallet.rounds].slice(0,500);
  const s = wallet.stats;
  s.wagers += bet; s.games += 1; s.winnings += adjustedPayout; s.losses += Math.max(0, bet - adjustedPayout);
  const won = adjustedPayout > bet;
  s.wins += won ? 1 : 0; s.streak = won ? s.streak + 1 : 0;
  s.lossStreak = adjustedPayout < bet ? s.lossStreak + 1 : 0;
  s.longestStreak = Math.max(s.longestStreak, s.streak);
  s.longestLossStreak = Math.max(s.longestLossStreak, s.lossStreak);
  s.biggestWin = Math.max(s.biggestWin, adjustedPayout - bet);
  s.biggestLoss = Math.max(s.biggestLoss, bet - adjustedPayout);
  const g = s.byGame[game] ?? { games: 0, wins: 0, wagered: 0, winnings: 0, biggestWin: 0 };
  g.games += 1; g.wins += won ? 1 : 0; g.wagered += bet; g.winnings += adjustedPayout;
  g.biggestWin = Math.max(g.biggestWin, adjustedPayout - bet); s.byGame[game] = g;
  save.xp += save.mode === 'creative' ? 0 : Math.max(2, Math.round(bet / 20));
  evaluateAchievements(save);
  return adjustedPayout;
}

export function addCreative(save: Save, amount: number) {
  if (save.mode !== 'creative') throw Error('Creative Mode only');
  const limit = Math.min(100_000, save.creativeFundLimit);
  if (!Number.isFinite(amount) || amount <= 0 || amount > limit) throw Error(`Enter an amount from $1 to ${money(limit)}`);
  transact(save, amount, 'System', 'Creative sandbox funds');
}
export function restart(save: Save) {
  const wallet = save.wallets[save.mode];
  if (save.mode === 'creative' || wallet.balance !== 0) throw Error('Bankroll can only restart at exactly $0');
  transact(save, START, 'System', 'Restarted bankroll');
}
export function purchaseItem(save: Save, itemId: string, category: string, price: number) {
  if (save.owned.includes(itemId)) throw Error('Item already owned');
  if (!Number.isFinite(price) || price < 0) throw Error('Invalid shop price');
  if (save.wallets[save.mode].balance < price) throw Error('Not enough Stack Cash');
  transact(save, -price, 'Shop', `Purchased ${itemId}`);
  save.owned.push(itemId); save.equipped[category] = itemId; evaluateAchievements(save);
}
export function purchaseMilestone(save: Save, id: string, requirement: number) {
  if (save.mode !== 'regular') throw Error('Prestige milestones are purchased in Regular Mode');
  if (save.milestones.includes(id)) throw Error('Milestone already purchased');
  if (save.wallets.regular.stats.highest < requirement) throw Error('Milestone requirement not reached');
  save.milestones.push(id);
}
export function evaluateAchievements(save: Save) {
  if (save.mode === 'creative') return [];
  const s = save.wallets[save.mode].stats;
  const checks: [string, boolean][] = [
    ['first-play', s.games >= 1], ['ten-rounds', s.games >= 10], ['winner', s.wins >= 5],
    ['hot-streak', s.longestStreak >= 5], ['collector', save.owned.length >= 5],
    ['high-roller', s.highest >= 100000], ['competitive-bronze', save.mode === 'competitive' && save.xp >= 500],
    ['plinko-pilot', (s.byGame.Plinko?.wins ?? 0) >= 5], ['mine-scout', (s.byGame.Mines?.wins ?? 0) >= 5],
    ['track-star', (s.byGame['Horse Racing']?.wins ?? 0) >= 5], ['coin-caller', (s.byGame['Coin Flip']?.wins ?? 0) >= 5],
    ['table-ace', (s.byGame.Blackjack?.wins ?? 0) >= 5], ['wager-10k', s.wagers >= 10000],
    ['winner-25k', s.winnings >= 25000],
  ];
  const unlocked: string[] = [];
  for (const [id, done] of checks) if (done && !save.achievements.some(a => a.id === id)) {
    save.achievements.push({ id, completedAt: Date.now() }); unlocked.push(id);
  }
  return unlocked;
}
// Large boards earn a growing bonus so their extra space feels rewarding.
export const minesMultiplier = (tiles: number, mines: number, safe: number) => {
  if (safe === 0) return 1;
  if (tiles < 2 || mines < 1 || mines >= tiles || safe > tiles - mines) return 0;
  const survival = Array.from({ length: safe }, (_, i) => (tiles - mines - i) / (tiles - i)).reduce((a, b) => a * b, 1);
  const boardSize = Math.sqrt(tiles);
  const largeBoardBoost = 1 + Math.max(0, boardSize - 5) * .2;
  return Number(((1 / survival) * 1.08 * largeBoardBoost).toFixed(2));
};
export type ChickenRisk='Easy'|'Medium'|'Hard'|'Extreme';
export const chickenSurvival = (risk:ChickenRisk) => ({Easy:.93,Medium:.84,Hard:.72,Extreme:.58})[risk];
export const chickenMultiplier = (steps:number,risk:ChickenRisk,extremeOdds=false) => {
  if (!Number.isInteger(steps) || steps < 0 || steps > 12) throw Error('Invalid crossing step');
  if (steps === 0) return 1;
  return Number(((.98 / chickenSurvival(risk) ** steps) * (extremeOdds ? 15 : 1)).toFixed(2));
};
export const golfMultiplier=(target:number,difficulty:number)=>{
  if(!Number.isInteger(target)||target<1||target>6)throw Error('Invalid stroke target');
  if(!Number.isFinite(difficulty)||difficulty<0||difficulty>1)throw Error('Invalid hole difficulty');
  const base=[0,250,35,8,3,1.6,1.15][target];
  return Number((base*(1+difficulty*.65)).toFixed(2));
};
export const golfRequiredStrokes=(difficulty:number,rng:()=>number=random)=>{
  if(!Number.isFinite(difficulty)||difficulty<0||difficulty>1)throw Error('Invalid hole difficulty');
  // Player-favorable score curve: aces remain special, but two- and
  // three-stroke finishes are substantially more common on every course.
  const roll=rng(),thresholds=[.07-difficulty*.03,.32-difficulty*.10,.66-difficulty*.16,.86-difficulty*.08,.96-difficulty*.04,.995];
  const result=thresholds.findIndex(t=>roll<t);
  return result<0?7:result+1;
};
export const plinkoPayout = (bet: number, multipliers: number[], slot: number) => {
  if (!Number.isInteger(slot) || slot < 0 || slot >= multipliers.length) throw Error('Invalid Plinko slot');
  return bet * multipliers[slot];
};
export type PlinkoRisk='Safe'|'Low'|'Medium'|'High'|'Extreme';
export function plinkoMultipliers(rows:number,risk:PlinkoRisk){
  if(![8,12,16].includes(rows))throw Error('Unsupported Plinko board size');
  const center=rows/2,profile={Safe:{power:1.2,floor:.08},Low:{power:2.5,floor:.03},Medium:{power:5,floor:.008},High:{power:10,floor:.001},Extreme:{power:18,floor:.0001}}[risk];
  const probabilities=Array.from({length:rows+1},(_,slot)=>binomial(rows,slot)/2**rows);
  const raw=probabilities.map((_,slot)=>profile.floor+Math.pow(Math.abs(slot-center)/center,profile.power));
  const scale=.96/raw.reduce((sum,value,slot)=>sum+value*probabilities[slot],0);
  const table=raw.map((value,slot)=>{const distance=Math.abs(slot-center)/center,sideBoost=slot===center?1:1.5+distance*1.5;return Number((value*scale*sideBoost).toFixed(2))});
  // The two slots touching the middle are the board's penalty pockets on
  // every row count and risk tier. All other payouts stay unchanged.
  const penalty=Number((table[center]*.45).toFixed(2));
  table[center-1]=penalty;table[center+1]=penalty;
  // A small universal boost applies to every slot, risk tier, row count, and mode.
  return table.map(multiplier=>Number((multiplier*1.06).toFixed(2)));
}
const binomial=(n:number,k:number)=>{let result=1;for(let i=1;i<=k;i++)result=result*(n-i+1)/i;return result};
export const plinkoRtp=(rows:number,risk:PlinkoRisk)=>plinkoMultipliers(rows,risk).reduce((sum,multiplier,slot)=>sum+multiplier*binomial(rows,slot)/2**rows,0);
export function crashPoint(rng:()=>number=random){const u=rng();if(u<.01)return 1;return Math.min(1000,Math.max(1,Math.floor((.99/(1-u))*100)/100))}
export function generatePlinkoPath(rows:number,rng:()=>number=random){
  if(![8,12,16].includes(rows))throw Error('Unsupported Plinko board size');
  const spacing=88/rows,pegs=plinkoPegLayout(rows);let rights=0,index=0;const points=[{x:50,y:2}],directions:('R'|'L')[]=[];
  for(let row=0;row<rows;row++){
    const peg=pegs.find(p=>p.row===row&&p.index===index)!;points.push({x:peg.x,y:peg.y});
    const right=rng()>=.5;directions.push(right?'R':'L');if(right){rights++;index++}
    points.push({x:Number((peg.x+(right?1:-1)*spacing*.2).toFixed(2)),y:Number((peg.y+2.2).toFixed(2))});
  }
  const slot=rights,slotX=50+(slot-rows/2)*spacing;points.push({x:Number(slotX.toFixed(2)),y:97});
  return{slot,rights,points,directions};
}
export function plinkoPegLayout(rows:number){if(![8,12,16].includes(rows))throw Error('Unsupported Plinko board size');const spacing=88/rows;return Array.from({length:rows},(_,row)=>Array.from({length:row+1},(_,index)=>({row,index,x:Number((50+(index-row/2)*spacing).toFixed(2)),y:Number((10+row*(76/(rows-1))).toFixed(2))}))).flat()}
export const coinPayout = (bet: number, choice: 'Heads'|'Tails', outcome: 'Heads'|'Tails') => choice === outcome ? bet * 1.96 : 0;
export const horsePayout = (bet: number, picked: number, winner: number, odds: number[]) => picked === winner ? bet * odds[picked] : 0;
export const canSplit = (cards: string[]) => cards.length === 2 && cardRankValue(cards[0]) === cardRankValue(cards[1]);
export const canDouble = (cards: string[], bet: number, balance: number) => cards.length === 2 && bet * 2 <= balance;
const cardRankValue = (card: string) => { const rank=card.slice(0,-1); return rank==='A'?11:['K','Q','J'].includes(rank)?10:Number(rank); };
export const handTotal = (cards: string[]) => {
  let total = 0, aces = 0;
  for (const card of cards) {
    const rank = card.slice(0, -1);
    if (rank === 'A') { total += 11; aces += 1; }
    else total += ['K', 'Q', 'J'].includes(rank) ? 10 : Number(rank);
  }
  while (total > 21 && aces) { total -= 10; aces -= 1; }
  return total;
};
export const isSoftHand=(cards:string[])=>{let raw=0,aces=0;for(const card of cards){const rank=card.slice(0,-1);if(rank==='A'){raw+=11;aces++}else raw+=['K','Q','J'].includes(rank)?10:Number(rank)}while(raw>21&&aces){raw-=10;aces--}return aces>0&&raw<=21};
